/**
 * Code-grant panel on the tenant detail (OCP-911, OCP-917).
 *
 * A workspace provisioned from an invite / prepaid code runs on the code's
 * plan until `license_expires_at`. This panel shows where the grant stands
 * (running / expiring / in grace / shut down / covered by a subscription /
 * lifetime / scratch), the grace end, the day the workspace shuts down and
 * the end of its 12.3 export window, core's read-only lock as delivered, the
 * reminder stages the grant gets and the ones already mailed, the licence env
 * the workspace runs with, and extends the grant with a required, audited
 * reason - the same route the `ocp_extend_grant` MCP tool calls (OCP-599). A
 * managed workspace is shut down after the grace, never run as Community
 * (that is the self-host edition).
 *
 * Self-contained: loads `GET /v1/admin/tenants/:id/code-grant`; a 404 means
 * the tenant is not a code workspace and the panel renders nothing.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api, ApiError } from '../lib/api';
import { datetimeLocalToIso, fmtDate, fmtDateTime } from '../lib/format-time';

export type CodeGrantState = 'running' | 'expiring' | 'in_grace' | 'shut_down' | 'covered' | 'lifetime' | 'scratch';

/** OCP-917 - core's subscription-state lock as OCP delivered it. */
export interface WorkspaceLock {
  desired: 'active' | 'cancelled';
  rendered: 'active' | 'cancelled';
  locked: boolean;
  written_at: string | null;
  pending_at: string | null;
  error: string | null;
}

export interface CodeGrantMail {
  stage: string;
  grant_ends_at: string;
  status: 'sent' | 'failed';
  sent_at: string;
  recipient: string;
  locale: string;
  error: string | null;
}

export interface CodeGrant {
  tenant_id: string;
  subdomain: string;
  workspace_host: string;
  customer_email: string | null;
  code: string | null;
  code_kind: 'invite' | 'prepaid' | null;
  tier: 'team' | 'business' | null;
  seats: number | null;
  granted_at: string;
  grant_ends_at: string | null;
  grace_ends_at: string | null;
  grace_stamped: boolean;
  shut_down_at: string | null;
  shuts_down_at: string | null;
  export_until: string | null;
  workspace_lock: WorkspaceLock;
  purpose: string | null;
  paused_at: string | null;
  subscription_state: string;
  state: CodeGrantState;
  days_until_end: number | null;
  covered_by: { subscription_id: string; status: string; tier: string; seat_count: number } | null;
  converted: { subscription_id: string; at: string | null } | null;
  reminder_stages: string[];
  mails: CodeGrantMail[];
  env: { value: string | null; tier: string | null; written_at: string | null; restart_pending_at: string | null };
  convert_url: string;
}

export const GRANT_STATE_LABEL: Record<CodeGrantState, string> = {
  running: 'running',
  expiring: 'ends within 30 days',
  in_grace: 'in grace',
  shut_down: 'shut down (paused, read-only)',
  covered: 'covered by subscription',
  lifetime: 'lifetime',
  scratch: 'scratch (outside the lifecycle)',
};

export const GRANT_STATE_CLASS: Record<CodeGrantState, string> = {
  running: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300',
  expiring: 'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200',
  in_grace: 'bg-orange-50 text-orange-900 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-200',
  shut_down: 'bg-red-50 text-red-800 ring-red-200 dark:bg-red-900/30 dark:text-red-300',
  covered: 'bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-900/30 dark:text-sky-300',
  lifetime: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300',
  scratch: 'bg-gray-100 text-gray-700 ring-gray-300 dark:bg-gray-800 dark:text-gray-300',
};

/** OCP-917 - one line on the lock: what the running workspace has, and whether a change is still on its way. */
export function lockText(lock: WorkspaceLock): string {
  if (lock.pending_at) {
    return `${lock.desired === 'cancelled' ? 'lock' : 'unlock'} pending since ${fmtDateTime(lock.pending_at)}${lock.error ? ` (${lock.error})` : ''}`;
  }
  if (lock.desired !== lock.rendered) return `${lock.desired === 'cancelled' ? 'lock' : 'unlock'} not delivered yet (the 5-minute sweep delivers it)`;
  return lock.rendered === 'cancelled' ? 'locked: read-only, closed page' : 'open';
}

export function GrantStatePill({ state }: { state: CodeGrantState }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${GRANT_STATE_CLASS[state]}`}>
      {GRANT_STATE_LABEL[state]}
    </span>
  );
}

const STAGE_LABEL: Record<string, string> = {
  expiring_30: '30 d',
  expiring_14: '14 d',
  expiring_7: '7 d',
  expiring_1: '1 d',
  in_grace: 'grace',
  shut_down: 'shutdown',
};

/** The reminder stages for the CURRENT grant end, each marked sent / failed / open. */
export function StageChips({ grant }: { grant: CodeGrant }) {
  const forEnd = grant.mails.filter((m) => m.grant_ends_at === grant.grant_ends_at);
  if (grant.state === 'scratch') return <span className="text-xs text-gray-500">none (scratch)</span>;
  const stages = [...grant.reminder_stages, 'in_grace', 'shut_down'];
  return (
    <span className="inline-flex flex-wrap gap-1">
      {stages.map((s) => {
        const sent = forEnd.find((m) => m.stage === s && m.status === 'sent');
        const failed = !sent && forEnd.find((m) => m.stage === s && m.status === 'failed');
        const cls = sent
          ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300'
          : failed
            ? 'bg-red-50 text-red-800 ring-red-200 dark:bg-red-900/30 dark:text-red-300'
            : 'bg-white text-gray-500 ring-gray-200 dark:bg-gray-900 dark:text-gray-400 dark:ring-gray-700';
        return (
          <span
            key={s}
            title={sent ? `sent ${fmtDateTime(sent.sent_at)} (${sent.locale})` : failed ? `failed: ${failed.error ?? 'unknown'}` : 'not sent'}
            className={`rounded px-1.5 py-0.5 text-[11px] ring-1 ring-inset ${cls}`}
          >
            {STAGE_LABEL[s] ?? s}
          </span>
        );
      })}
    </span>
  );
}

type ExtendMode = 'months' | 'date' | 'lifetime';

export function CodeGrantPanel({ tenantId }: { tenantId: string }) {
  const [grant, setGrant] = useState<CodeGrant | null>(null);
  const [absent, setAbsent] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<ExtendMode>('months');
  const [months, setMonths] = useState('3');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api<CodeGrant>(`/v1/admin/tenants/${tenantId}/code-grant`);
      // An answer that is not a code grant (an api out of step with this UI)
      // is reported, not rendered half.
      if (!r || typeof r.state !== 'string' || !Array.isArray(r.mails) || !Array.isArray(r.reminder_stages) || !r.env) {
        setLoadError('The code grant answer had an unexpected shape.');
        return;
      }
      setGrant(r);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setAbsent(true);
      else setLoadError(err instanceof ApiError ? `Could not load the code grant (HTTP ${err.status}).` : 'Could not load the code grant.');
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setResult(null);
    try {
      const body: Record<string, unknown> = { reason: reason.trim() };
      if (mode === 'months') body.months = Number(months);
      else if (mode === 'date') body.expires_at = datetimeLocalToIso(`${date}T23:59`) ?? new Date(`${date}T23:59:00`).toISOString();
      else body.lifetime = true;
      const r = await api<{ grant: CodeGrant; env: { outcome: string; detail?: string } }>(
        `/v1/admin/tenants/${tenantId}/code-grant/extend`,
        { method: 'POST', body },
      );
      setGrant(r.grant);
      setReason('');
      const back = grant?.subscription_state === 'paused' && r.grant.subscription_state !== 'paused';
      setResult(
        back
          ? 'Extended. The shut-down workspace is back: paused state ended, the read-only lock is lifted with the plan in one restart.'
          : r.env.outcome === 'restarted'
          ? 'Extended. The workspace was redeployed once to restore the plan.'
          : r.env.outcome === 'restart_pending'
            ? `Extended. The restoring redeploy could not start yet (${r.env.detail ?? 'deploy in flight'}); the hourly sweep retries it.`
            : r.env.outcome === 'failed'
              ? `Extended, but the licence env could not be written (${r.env.detail ?? 'error'}); the hourly sweep retries it.`
              : 'Extended. The licence env is up to date (no restart needed).',
      );
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; message?: string } | null) : null;
      setSaveError(body?.message ?? body?.error ?? (err instanceof ApiError ? `Extension failed (HTTP ${err.status}).` : 'Extension failed.'));
    } finally {
      setSaving(false);
    }
  }, [tenantId, mode, months, date, reason, grant]);

  if (absent) return null;

  const reasonOk = reason.trim().length >= 10 && reason.trim().length <= 500;
  const modeOk = mode === 'months' ? Number(months) >= 1 && Number(months) <= 36 : mode === 'date' ? date !== '' : true;

  return (
    <section data-testid="code-grant-panel" className="mt-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Code grant</h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        The plan this workspace got from its code. Reminders go out 30 / 14 / 7 / 1 days before the end (only the stages in
        the second half of the grant), then a grace with the full plan. When the grace ends without a subscription the
        workspace is shut down: paused, read-only with the closed page, its data exportable in the portal under Backups
        for the AGB 12.3 window. A subscription of the account covers it instead and brings a shut-down workspace back;
        so does an extension. Scratch workspaces are outside this lifecycle.
      </p>
      {loadError && (
        <div role="alert" className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {loadError}
        </div>
      )}
      {grant && (
        <>
          <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Item label="State"><GrantStatePill state={grant.state} /></Item>
            <Item label="Code">
              <span className="font-mono">{grant.code ?? '-'}</span>
              {grant.tier && <span className="ml-1 text-gray-500">({grant.tier}, {grant.seats ?? 'unlimited'} seats)</span>}
            </Item>
            <Item label="Grant ends">
              {grant.grant_ends_at ? `${fmtDate(grant.grant_ends_at)}${grant.days_until_end !== null ? ` (${grant.days_until_end} d)` : ''}` : 'lifetime'}
            </Item>
            <Item label="Grace ends">
              {grant.grace_ends_at ? `${fmtDate(grant.grace_ends_at)}${grant.grace_stamped ? '' : ' (projected)'}` : '-'}
            </Item>
            <Item label={grant.state === 'shut_down' ? 'Shut down' : 'Shuts down'}>
              {grant.state === 'shut_down'
                ? `${fmtDate(grant.shuts_down_at ?? grant.paused_at)}${grant.subscription_state === 'paused' ? '' : ' (shutdown pending - the next sweep pauses it)'}`
                : grant.shuts_down_at
                  ? `${fmtDate(grant.shuts_down_at)} unless a subscription takes over`
                  : '-'}
            </Item>
            <Item label="Export until (AGB 12.3)">{grant.export_until ? fmtDate(grant.export_until) : '-'}</Item>
            <Item label="Workspace lock">{lockText(grant.workspace_lock)}</Item>
            <Item label="Subscription">
              {grant.covered_by
                ? `${grant.covered_by.tier}, ${grant.covered_by.seat_count} seats (${grant.covered_by.status})`
                : 'none'}
              {grant.converted && <span className="block text-xs text-gray-500">converted from this workspace {grant.converted.at ? fmtDate(grant.converted.at) : ''}</span>}
            </Item>
            <Item label="Reminder stages"><StageChips grant={grant} /></Item>
            <Item label="Licence env">
              <span className="font-mono text-xs">
                LICENSE_EXPIRES_AT={grant.env.value === null ? '(not recorded)' : grant.env.value || "''"}
              </span>
              <span className="block font-mono text-xs">LICENSE_TIER={grant.env.tier === null ? '(not recorded)' : grant.env.tier || "''"}</span>
              {grant.env.restart_pending_at && (
                <span className="block text-xs text-amber-700 dark:text-amber-300">restart pending since {fmtDateTime(grant.env.restart_pending_at)}</span>
              )}
            </Item>
            <Item label="Customer link"><a className="text-brand-500 hover:underline" href={grant.convert_url} target="_blank" rel="noreferrer">portal conversion</a></Item>
          </dl>

          {grant.mails.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-gray-700 dark:text-gray-300">Lifecycle mails ({grant.mails.length})</summary>
              <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
                {grant.mails.map((m) => (
                  <li key={`${m.stage}-${m.grant_ends_at}-${m.sent_at}`}>
                    {fmtDateTime(m.sent_at)} - {STAGE_LABEL[m.stage] ?? m.stage} for the end {fmtDate(m.grant_ends_at)} - {m.status} to {m.recipient} ({m.locale})
                    {m.error ? ` - ${m.error}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {grant.state === 'scratch' ? (
            <p className="mt-5 border-t border-gray-100 dark:border-gray-800 pt-4 text-sm text-gray-600 dark:text-gray-400">
              Scratch workspace: no reminders, no grace, no shutdown, no extension. Its scratch expiry on the tenant governs it.
            </p>
          ) : (
          <div className="mt-5 space-y-3 border-t border-gray-100 dark:border-gray-800 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Extend the grant</h3>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {(['months', 'date', 'lifetime'] as ExtendMode[]).map((m) => (
                <label key={m} className="inline-flex items-center gap-2">
                  <input type="radio" name="grant-extend-mode" value={m} checked={mode === m} onChange={() => setMode(m)} />
                  <span>{m === 'months' ? 'by months' : m === 'date' ? 'until a date' : 'lifetime'}</span>
                </label>
              ))}
            </div>
            {mode === 'months' && (
              <label className="block max-w-xs text-sm">
                <span className="text-gray-700 dark:text-gray-300">Months (from the current end, or from today when it already ran out)</span>
                <input type="number" min={1} max={36} value={months} onChange={(e) => setMonths(e.target.value)} aria-label="Months" className="mt-1 w-24 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm" />
              </label>
            )}
            {mode === 'date' && (
              <label className="block max-w-xs text-sm">
                <span className="text-gray-700 dark:text-gray-300">New end</span>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="New end" className="mt-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm" />
              </label>
            )}
            <label className="block text-sm">
              <span className="text-gray-700 dark:text-gray-300">Reason (recorded in the audit log, 10..500 characters)</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} aria-label="Reason" className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm" />
            </label>
            {saveError && (
              <div role="alert" className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {saveError}
              </div>
            )}
            {result && <div role="status" className="text-sm font-medium text-green-700 dark:text-green-400">{result}</div>}
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || !reasonOk || !modeOk}
              className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Extending…' : 'Extend grant'}
            </button>
          </div>
          )}
        </>
      )}
    </section>
  );
}

function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}
